import React, { useState, useEffect, useRef } from 'react';
import { Task, Quadrant, TaskStatus } from '../types';
import { TaskCard } from '../components/TaskCard';
import { Plus, Sparkles } from 'lucide-react';
import { QUADRANT_LABELS, QUADRANT_COLORS } from '../constants';
import { Button } from '../components/Button';

interface MatrixViewProps {
  tasks: Task[];
  onAddTask: (title: string, quadrant: Quadrant) => void;
  onMoveTask: (taskId: string, quadrant: Quadrant) => void;
  onDeleteTask: (id: string) => void;
  onFocusTask: (id: string) => void;
  onOpenMagicSort?: () => void;
  isPro: boolean;
}

export const MatrixView: React.FC<MatrixViewProps> = ({ 
  tasks, 
  onAddTask, 
  onMoveTask, 
  onDeleteTask,
  onFocusTask,
  onOpenMagicSort,
  isPro
}) => {
  // Only show non-done tasks in Matrix usually, but let's show all or filtered.
  // Standard Matrix focuses on "What to do".
  const activeTasks = tasks.filter(t => t.status !== TaskStatus.Done);

  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  
  // Ref for the Do (Q1) input to handle the "N" shortcut
  const doInputRef = useRef<HTMLInputElement>(null);

  // Keyboard Shortcut: N -> Focus "Do First" input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is already typing in an input or textarea
      if (
        document.activeElement instanceof HTMLInputElement || 
        document.activeElement instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key.toLowerCase() === 'n') {
        e.preventDefault();
        doInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleInputChange = (quadrant: string, value: string) => {
    setInputValues(prev => ({ ...prev, [quadrant]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, quadrant: Quadrant) => {
    if (e.key === 'Enter' && inputValues[quadrant]?.trim()) {
      onAddTask(inputValues[quadrant], quadrant);
      setInputValues(prev => ({ ...prev, [quadrant]: '' }));
    }
  };

  const handleDrop = (e: React.DragEvent, targetQuadrant: Quadrant) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) {
      onMoveTask(taskId, targetQuadrant);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const renderQuadrant = (quadrant: Quadrant) => {
    const qTasks = activeTasks.filter(t => t.quadrant === quadrant);
    
    // Attach ref only to the first quadrant (Do)
    const isDoQuadrant = quadrant === Quadrant.Do;
    
    return (
      <div 
        className={`flex flex-col h-full rounded-xl border p-4 transition-colors ${QUADRANT_COLORS[quadrant].replace('text-', 'border-').split(' ')[1]}`}
        onDrop={(e) => handleDrop(e, quadrant)}
        onDragOver={handleDragOver}
      >
        <h3 className={`font-bold mb-3 uppercase tracking-wider text-xs ${QUADRANT_COLORS[quadrant].split(' ').pop()}`}>
          {QUADRANT_LABELS[quadrant]}
        </h3>
        
        <div className="flex-1 overflow-y-auto min-h-[100px]">
          {qTasks.map(task => (
            <TaskCard 
              key={task.id} 
              task={task} 
              onDelete={onDeleteTask} 
              onFocus={onFocusTask}
              isCompact 
            />
          ))}
          {qTasks.length === 0 && (
            <div className="h-full flex items-center justify-center text-gray-400 text-xs italic border-2 border-dashed border-gray-200 rounded-lg m-1">
              Drop tasks here
            </div>
          )}
        </div>

        <div className="mt-3 relative">
          <input
            ref={isDoQuadrant ? doInputRef : undefined}
            type="text"
            placeholder={isDoQuadrant ? "Add task... (Press 'N')" : "Add task..."}
            className="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={inputValues[quadrant] || ''}
            onChange={(e) => handleInputChange(quadrant, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, quadrant)}
          />
          <button 
            onClick={() => {
               if(inputValues[quadrant]?.trim()) {
                 onAddTask(inputValues[quadrant], quadrant);
                 setInputValues(prev => ({ ...prev, [quadrant]: '' }));
               }
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header with Magic Sort */}
      <div className="flex items-center justify-between flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-800">My Matrix</h2>
        {onOpenMagicSort && (
          <Button 
             variant="ghost" 
             size="sm" 
             onClick={onOpenMagicSort}
             className="text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200"
          >
             <Sparkles size={16} className="mr-2" />
             AI Magic Sort
          </Button>
        )}
      </div>

      <div className="flex-1 grid grid-rows-2 grid-cols-1 md:grid-cols-2 gap-4 min-h-0">
        {renderQuadrant(Quadrant.Do)}
        {renderQuadrant(Quadrant.Schedule)}
        {renderQuadrant(Quadrant.Delegate)}
        {renderQuadrant(Quadrant.Eliminate)}
      </div>
    </div>
  );
};